import React, { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useNotification } from 'hooks'
import { usePartnerQuery } from '../../api'
import { InfoItems } from 'components/core-ui/InfoItems'
import { Box, CircularProgress, Grid } from '@mui/material'
import style from './../../partners.modules.scss'
import ActionComponents from '../../components/ActionComponents'
import { formatUIDateTime } from 'utils/helpers'
import EditAssignmentModal from '../Assignments/EditAssignment/EditAssignmentModal'
import EditPartnerModal from '../EditPartner/EditPartnerModal'
import { DeleteModal } from '../components/DeleteModal'
import { getActiveAssignment } from '../helpers'
import ViewActionIcon from 'icons/ViewActionIcon'
import { columns as defaultColumns } from './helpers'
import { AddActionIcon } from 'icons'
import { Link } from 'components/core-ui/Link'
import { URLS } from 'components/iam/constants'
import { is } from 'immer/dist/internal'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { Table } from 'components/core-ui/Table'

const PartnerDetails: React.FC = () => {
  const [isAddAssignmentModalOpen, setIsAddAssignmentModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const { canEdit, canDelete } = { canEdit: true, canDelete: false }
  const [tableItems, setTableItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)

  const { id } = useParams<{ id: string }>()
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const {
    data: partner,
    isLoading: isPartnerLoading,
    refetch: refetchPartner
  } = usePartnerQuery(showNotification, Number(id), shouldRefreshData)

  const columns = useMemo(() => defaultColumns, [])

  const userName = useMemo(() => {
    if (!partner) return ''
    const activeAssignment = partner.assignmentList.find(
      assignment => assignment.status.name === 'Active'
    )
    return activeAssignment?.userName || ''
  }, [partner])

  const openEditModal = () => setIsEditModalOpen(true)
  const closeEditModal = () => setIsEditModalOpen(false)

  const openAddAssignmentModal = () => setIsAddAssignmentModalOpen(true)
  const closeAddAssignmentModal = () => setIsAddAssignmentModalOpen(false)

  const openDeleteModal = () => setIsDeleteModalOpen(true)
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false)
    navigate(-1)
  }

  if (isPartnerLoading)
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  if (!partner) return <div>Partner not found</div>

  const mainAssignment = getActiveAssignment(partner.assignmentList)
  const activeAssignment = getActiveAssignment(partner.assignmentList, true)

  const fetchData = async ({ page, rowsPerPage, order, orderBy, searchText }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/iam/external-partners/assignments',
          tableFilters: [{ name: 'person.id', value: id }],
          page,
          rowsPerPage,
          order,
          orderBy,
          searchText,
          searchFields: ['globalId', 'jobTitle', 'person.fullName']
        })
      )

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `There was an error fetching assignments. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  return (
    <div className={style.mainContainer}>
      <div className={style.breadcrumbsContainer}>
        <Link href="/iam/partners" className={style.breadcrumbsLink}>
          External Partners Management
        </Link>
        <span className={style.breadcrumbsSeparator}>|</span>
        <Link href={URLS.IAM_PARTNERS_MANAGEMENT} className={style.breadcrumbsLink}>
          Partners
        </Link>
        <span className={style.breadcrumbsSeparator}>|</span>
        <span className={style.breadcrumbsCurrent}>
          {partner.globalId} - {partner.firstName + ' ' + partner.lastName}
        </span>
      </div>
      {canEdit && (
        <>
          <EditPartnerModal
            isModalOpen={isEditModalOpen}
            onModalClose={closeEditModal}
            partnerId={partner.id}
            onSuccess={refetchPartner}
          />
          <EditAssignmentModal
            isModalOpen={isAddAssignmentModalOpen}
            onModalClose={closeAddAssignmentModal}
            partnerId={partner.id}
            setShouldRefreshData={setShouldRefreshData}
            partnerType={activeAssignment?.externalPartnerType}
          />
        </>
      )}
      {canDelete && (
        <DeleteModal
          isModalOpen={isDeleteModalOpen}
          closeModal={closeDeleteModal}
          itemId={partner.id}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}

      <Box className={style.flexContainer}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <InfoItems
            itemsPerRow={1}
            itemsSpacing={2}
            items={[
              { name: mainAssignment?.externalPartnerType.name, label: 'Partner Type:' },
              { name: partner.firstName, label: 'First Name:' },
              { name: partner.lastName, label: 'Last Name:' },
              { name: partner.phone || 'Not provided', label: 'Phone:' },
              { name: partner.email || 'Not provided', label: 'External Email:' },
              {
                name:
                  mainAssignment?.email && mainAssignment.email !== 'null'
                    ? mainAssignment.email
                    : '',
                label: 'Internal Email:'
              },
              { name: mainAssignment?.status.name, label: 'Status:' }
            ]}
            title={'Partner details'}
            actionComponents={<ActionComponents openEditModal={openEditModal} />}
          />
        </Box>
        <Box className={style.infoItemsContainer}>
          <InfoItems
            itemsPerRow={2}
            itemsSpacing={2}
            items={[
              { name: partner.createdBy, label: 'Created By:' },
              { name: formatUIDateTime(partner.createdDate), label: 'Created Date:' },
              { name: partner.lastModifiedBy, label: 'Modified By:' },
              { name: formatUIDateTime(partner.lastModifiedDate), label: 'Modified Date:' },
              { name: partner.globalId, label: 'Global ID:' },
              { name: userName, label: 'User name:' }
            ]}
            title={'Partner metadata'}
          />
          <InfoItems
            itemsPerRow={2}
            itemsSpacing={2}
            items={[
              {
                name: `${mainAssignment?.legalEntity?.name}`,
                label: 'Name:',
                render: () => (
                  <Link
                    href={`/iam/externalPartners/legalEntities/${mainAssignment?.legalEntity?.id}`}
                    style={{ textDecoration: 'none' }}
                  >
                    {mainAssignment?.legalEntity?.name}
                  </Link>
                )
              },
              { name: mainAssignment?.legalEntity?.country?.name, label: 'Country:' },
              { name: mainAssignment?.legalEntity?.code, label: 'CIF:' }
            ]}
            title="Legal entity details"
          />
        </Box>
      </Box>
      {partner.details && (
        <Grid container spacing={2} style={{ marginTop: '4px' }}>
          <Grid item xs={12}>
            <InfoItems
              itemsPerRow={1}
              itemsSpacing={2}
              items={[
                ...(partner.details
                  ? [
                      {
                        name: partner.details,
                        label: 'Partner details:',
                        labelSize: 1,
                        valueSize: 11
                      }
                    ]
                  : [])
              ]}
              title={'Partner extra information'}
            />
          </Grid>
        </Grid>
      )}
      <Box sx={{ display: 'flex', marginTop: '24px' }}>
        <Table
          items={tableItems}
          fetchData={fetchData}
          isLoading={isLoading}
          totalCount={totalCount}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
          shouldResetPage={shouldResetPage}
          setShouldResetPage={setShouldResetPage}
          columns={columns}
          hideCheckbox
          orderBy="createdDate"
          order="desc"
          title="Assignments"
          toolbarActions={
            activeAssignment
              ? []
              : [
                  {
                    label: 'Add Assignment',
                    icon: <AddActionIcon />,
                    onClick: openAddAssignmentModal
                  }
                ]
          }
          rowActions={[
            {
              label: 'Details',
              labelColor: 'white',
              typographyVariant: 'medium',
              desktopIcon: <ViewActionIcon />,
              onClick: (_e, rowId) => navigate(`/iam/externalPartners/assignments/${rowId}`),
              backgroundColor: ''
            }
          ]}
          rowActionsWidth="80px"
          desktopRowActionsWidth="50px"
        />
      </Box>
    </div>
  )
}

export default PartnerDetails
